import { Route, Routes, useNavigate } from 'react-router-dom';

import AdminPrivateRoute from './AdminPrivateRoute';
import HeaderHelmet from 'modules/common/Decorators/HeaderHelmet';
import NotFoundPage from 'modules/common/NotFoundPage';
import PrivateRoutesLoader from './PrivateRoutesLoader';
import PublicRoutesLoader from 'modules/common/Routes/components/PublicRoutesLoader';
import { adminRoutes } from 'webApp/modules/Admin/routes';
import {
  selectOfficeDetails,
  selectUserType,
} from 'modules/Authentication/slice/selectors';
import { useSelector } from 'react-redux';
import { selectOrganisationUiError } from 'styles/OrganisationUi/slice/selectors';
import React, { useEffect } from 'react';
import DomainNotFoundPage from 'modules/common/DomainNotFound';
import InactiveUserPage from 'modules/common/InactiveUserPage';
import ProtectedRoute from './ProtectedRoute';
import UnauthorizedPage from 'modules/common/UnauthorizedPage';

export default function RoutesLoaderView({
  routes,
  showClientDomain,
  ...props
}) {
  const navigate = useNavigate();

  const orgUiError = useSelector(selectOrganisationUiError);
  const { isUserAdmin } = useSelector(selectOfficeDetails);

  const userType = useSelector(selectUserType);

  useEffect(() => {
    if (orgUiError) {
      navigate('/domain-not-found');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUiError]);

  return (
    <Routes>
      {routes?.map(({ component: Component, ...routeProps }, index) =>
        routeProps?.needsAuth ? (
          <Route
            key={`${routeProps?.path}-${routeProps?.title || 'auth'}-${index}`}
            path={routeProps?.path}
            element={
              routeProps?.to ? (
                <Component
                  {...routeProps}
                  to={
                    routeProps?.toAdmin && isUserAdmin
                      ? routeProps?.toAdmin
                      : routeProps?.to
                  }
                />
              ) : (
                <React.Fragment key={`private-${routeProps?.path}-${index}`}>
                  <HeaderHelmet {...routeProps} />
                  <PrivateRoutesLoader routeProps={routeProps} {...props}>
                    {/* <Component {...routeProps} {...props} /> */}
                    <ProtectedRoute
                      requiredPermissions={
                        routeProps?.requiredPermissions || []
                      }
                      userType={userType}
                    >
                      <Component {...routeProps} {...props} />
                    </ProtectedRoute>
                  </PrivateRoutesLoader>
                </React.Fragment>
              )
            }
          />
        ) : (
          <React.Fragment key={`public-${routeProps?.path}-${index}`}>
            {showClientDomain && routeProps?.showForClientDomain && (
              <Route
                key={`${routeProps?.path}-clientDomain-${index}`}
                path={routeProps?.path}
                element={
                  <React.Fragment
                    key={`clientDomain-${routeProps?.path}-${index}`}
                  >
                    {!routeProps?.disableHeader && (
                      <HeaderHelmet {...routeProps} />
                    )}
                    <PublicRoutesLoader {...routeProps}>
                      <Component {...routeProps} {...props} />
                    </PublicRoutesLoader>
                  </React.Fragment>
                }
              />
            )}
            {showClientDomain && !routeProps?.hideForClientDomain && (
              <Route
                key={`${routeProps?.path}-clientDomainVisible-${index}`}
                path={routeProps?.path}
                element={
                  <React.Fragment
                    key={`clientDomainVisible-${routeProps?.path}-${index}`}
                  >
                    {!routeProps?.disableHeader && (
                      <HeaderHelmet {...routeProps} />
                    )}
                    <PublicRoutesLoader {...routeProps}>
                      <Component {...routeProps} {...props} />
                    </PublicRoutesLoader>
                  </React.Fragment>
                }
              />
            )}
            {!showClientDomain && !routeProps?.showForClientDomain && (
              <Route
                key={`${routeProps?.path}-noClientDomain-${index}`}
                path={routeProps?.path}
                element={
                  <React.Fragment
                    key={`noClientDomain-${routeProps?.path}-${index}`}
                  >
                    {!routeProps?.disableHeader && (
                      <HeaderHelmet {...routeProps} />
                    )}
                    <PublicRoutesLoader {...routeProps}>
                      <Component {...routeProps} {...props} />
                    </PublicRoutesLoader>
                  </React.Fragment>
                }
              />
            )}
          </React.Fragment>
        ),
      )}

      {adminRoutes?.map(
        ({ component: AdminComponent, ...routeProps }, index) => (
          <Route
            key={`${routeProps?.path}-admin-${index}`}
            path={routeProps?.path}
            element={
              routeProps?.to ? (
                <AdminComponent {...routeProps} to={routeProps?.to} />
              ) : (
                <React.Fragment key={`admin-${routeProps?.path}-${index}`}>
                  {!routeProps?.disableHeader && (
                    <HeaderHelmet {...routeProps} />
                  )}
                  {routeProps?.needsAuth ? (
                    <AdminPrivateRoute>
                      <AdminComponent {...routeProps} {...props} />
                    </AdminPrivateRoute>
                  ) : (
                    <AdminComponent {...routeProps} {...props} />
                  )}
                </React.Fragment>
              )
            }
          />
        ),
      )}

      <Route
        key="unauthorizedPage"
        path="/unauthorized"
        element={
          <>
            <HeaderHelmet title="403" description="Unauthorized!" />
            <UnauthorizedPage />
          </>
        }
      />

      <Route
        key="notFoundPage"
        path="*"
        element={
          <>
            <HeaderHelmet title="404" description="Page Not Found!" />
            <NotFoundPage />
          </>
        }
      />

      <Route
        key="inactiveUserPage"
        path="/inactive-user"
        element={
          <>
            <HeaderHelmet title="403" description="Inactive User!" />
            <InactiveUserPage />
          </>
        }
      />

      <Route
        key="domainNotFoundPage"
        path="/domain-not-found"
        element={
          <>
            <HeaderHelmet title="404" description="Domain Not Found!" />
            <DomainNotFoundPage />
          </>
        }
      />
    </Routes>
  );
}
